import React from "react";
import { UpdateQuestionnaireDataType } from "./ConcludePractice";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ButtonConfirm } from "../../../../../Layout/Buttons";
import { fileToBase64 } from "../../../../../../utils/fileUtils";
import {
  DocumentFiles,
  PracticeAttachment,
} from "../../../../../../redux/apiSpecifications/apiCrud";
import Error from "../../../../../Modals/Error";
import Loading from "../../../../../Loading";

// Styled components
const SolveMissingAgreementWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  justify-content: space-evenly;
`;

const LoaderWrapper = styled.div`
  width: 100px;
  height: 100px;
`;

/**
 * @desccription
 * @interface ISolveMissingAgreementProps
 * @onUpdatePractice {(newValues: any) => void} - callback called to update some practice's values
 */

interface ISolveMissingAgreementProps {
  idPractice?: number;
  onUpdateQuestionnaire: (val: UpdateQuestionnaireDataType) => void;
}

/**
 * ConcludePractice component
 *
 * @component
 * @example
 * <ConcludePractice />
 */
const SolveMissingAgreement: React.FC<ISolveMissingAgreementProps> = ({
  idPractice,
  onUpdateQuestionnaire,
}) => {
  // Common variables
  const { t } = useTranslation();

  // Local state
  const refAgreementFile = React.useRef<HTMLInputElement>(null);
  const [loadingAgreement, setLoadingAgreement] =
    React.useState<boolean>(false);

  // Handles

  const selectAgreementFile = () => refAgreementFile.current?.click();

  const handleLoadAgreement = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e?.target?.files?.length) return;

    const selectedFile = e.target.files[0];
    const fileName = selectedFile.name.toLowerCase();

    const isValidExtension = [".pdf", ".jpg", ".jpeg"].some((ext) =>
      fileName.endsWith(ext)
    );

    if (!isValidExtension) {
      const invalidFileError = t(
        "MESSAGES.errors-unsupported-agreement-file-format"
      );
      Error({
        error: invalidFileError,
      });
      return;
    }

    setLoadingAgreement(true);

    try {
      const fileContent = await fileToBase64(selectedFile);

      const attachment: PracticeAttachment = {
        id: 0,
        name: selectedFile.name,
        originalName: selectedFile.name,
        base64File: fileContent,
      };

      const practiceDocument: DocumentFiles = {
        documentCode: "",
        documentTypeCode: "ACC",
        attachmentsFiles: [attachment],
      };

      onUpdateQuestionnaire({
        questionnaire: {
          agreement: "S",
        },
        documents: [practiceDocument],
        status: "questionnare",
      });
    } finally {
      setLoadingAgreement(false);
    }
  };

  const handleWaitingForAgreement = () => {
    onUpdateQuestionnaire({
      questionnaire: { agreement: "SN" },
      status: "questionnare",
    });
  };

  const handleVocalAgreement = () => {
    onUpdateQuestionnaire({
      questionnaire: { agreement: "N", isVocalAgreement: true },
      status: "questionnare",
    });
  };

  const handleNoAgreement = () => {
    onUpdateQuestionnaire({
      questionnaire: { agreement: "N" },
      status: "load-adz",
    });
  };

  return (
    <SolveMissingAgreementWrapper>
      {loadingAgreement && (
        <LoaderWrapper>
          <Loading />
        </LoaderWrapper>
      )}
      {!loadingAgreement && (
        <>
          <ButtonConfirm onClick={selectAgreementFile}>
            {
              t(
                "appraisals-conclude-practice-button-solve-missing-agreement-load"
              )!
            }
          </ButtonConfirm>

          <input
            data-testid="file-input"
            ref={refAgreementFile}
            type="file"
            accept=".pdf,.jpg,.jpeg"
            style={{ display: "none" }}
            onChange={(e) => handleLoadAgreement(e)}
          />

          <ButtonConfirm onClick={handleWaitingForAgreement}>
            {
              t(
                "appraisals-conclude-practice-button-solve-missing-agreement-waiting"
              )!
            }
          </ButtonConfirm>

          <ButtonConfirm onClick={handleVocalAgreement}>
            {
              t(
                "appraisals-conclude-practice-button-solve-missing-agreement-vocal"
              )!
            }
          </ButtonConfirm>

          <ButtonConfirm onClick={handleNoAgreement}>
            {
              t(
                "appraisals-conclude-practice-button-solve-missing-agreement-no"
              )!
            }
          </ButtonConfirm>
        </>
      )}
    </SolveMissingAgreementWrapper>
  );
};

export default SolveMissingAgreement;
